<template>
  <div class="review-page">



    <transition name="toast">
      <div v-if="toastMsg" :class="['toast', toastError ? 'toast-error' : 'toast-success']">
        {{ toastMsg }}
      </div>
    </transition>

    <div class="container">
      <div class="page-layout">

        <!-- 左侧：3D 只读预览 -->
        <div class="viewer-wrapper">
          <Viewer3D
            ref="viewer3d"
            :view-only="true"
            @model-loaded="onModelLoaded"
          />
        </div>

        <!-- 右侧面板 -->
        <div class="right-panel">
          <div class="panel-block comment-block">
            <Comments ref="comments" :current-garment="currentGarment" />
          </div>
          <div class="panel-block roster-block">
            <RosterManager ref="rosterManager" />
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { useDesignStore } from '@/stores/designStore'
import { mapState } from 'pinia'
import Viewer3D from '@/components/Viewer3D.vue'
import Comments from '@/components/Comments.vue'
import RosterManager from '@/components/RosterManager.vue'

export default {
  name: 'tshirts',
  components: { Viewer3D, Comments, RosterManager },

  data() {
    return {
      isLoading: false,
      toastMsg: '',
      toastError: false,
      _toastTimer: null,
    }
  },

  computed: {
    ...mapState(useDesignStore, ['currentGarment'])
  },

  mounted() {
    this.init()
  },

  methods: {
    async init() {
      const params = new URLSearchParams(window.location.search)
      const id = params.get('id')

      if (!id) {
        await this.$nextTick()
        if (this.$refs.viewer3d) this.$refs.viewer3d.loadGarment('T-shirt')
        return
      }

      this.isLoading = true
      try {
        const res = await fetch(`/load-design.php?id=${id}`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const result = await res.json()

        if (!result.success || !result.all_garments) {
          this.showToast('Design not found', true)
          return
        }

        const store = useDesignStore()
        store.currentDesignId = result.design_id || id

        // ── 1. 直接把 T-shirt 数据写入 store，不调用 loadDesignFromData
        //       (避免内部 switchGarment 触发模型加载，与下面的 loadGarment 冲突)
        const garmentData = result.all_garments['T-shirt'] || {}

        // 恢复颜色
        store.userColors.clear()
        if (garmentData.colors) {
          for (const [part, colorHex] of Object.entries(garmentData.colors)) {
            const colorNum = typeof colorHex === 'string'
              ? parseInt(colorHex.replace('#', ''), 16)
              : colorHex
            store.userColors.set(part, colorNum)
          }
        }

        // 恢复图层（logo + 文字）
        store.partLayersMap.clear()
        if (garmentData.logos && garmentData.logos.length > 0) {
          for (const logo of garmentData.logos) {
            const layers = store.partLayersMap.get(logo.part) || []
            layers.push({
              id: logo.id, url: logo.url, isText: false,
              scale: logo.scale || 1, offsetX: logo.offsetX || 0,
              offsetY: logo.offsetY || 0, rotation: logo.rotation || 0,
              img: null
            })
            store.partLayersMap.set(logo.part, layers)
          }
        }
        if (garmentData.texts && garmentData.texts.length > 0) {
          for (const text of garmentData.texts) {
            const layers = store.partLayersMap.get(text.part) || []
            layers.push({
              id: text.id, isText: true,
              textContent: text.text, textFont: text.font,
              textColor: text.color, fontSize: text.fontSize || 80,
              scale: text.scale || 0.8, offsetX: text.offsetX || 0,
              offsetY: text.offsetY || 0, rotation: text.rotation || 0,
              img: null
            })
            store.partLayersMap.set(text.part, layers)
          }
        }
        store.currentGarment = 'T-shirt'

        // ── 2. 触发模型加载，等待 partsMap 建立
        await this.$nextTick()
        const viewer = this.$refs.viewer3d
        if (viewer) {
          if (viewer.isLoading) {
            // 已在加载，等待完成
            await new Promise((resolve) => {
              const t = setInterval(() => {
                if (!viewer.isLoading && viewer.partsMap && viewer.partsMap.size > 0) {
                  clearInterval(t); resolve()
                }
              }, 100)
              setTimeout(() => { clearInterval(t); resolve() }, 8000)
            })
          } else if (!viewer.currentModel || viewer.partsMap.size === 0) {
            viewer.loadGarment('T-shirt')
            await new Promise((resolve) => {
              const t = setInterval(() => {
                if (viewer.currentModel && viewer.partsMap && viewer.partsMap.size > 0) {
                  clearInterval(t); resolve()
                }
              }, 100)
              setTimeout(() => { clearInterval(t); resolve() }, 8000)
            })
          }
        }

        // ── 3. 合成纹理（此时 partsMap 已建立）
        await this.compositeAllTextures()

        // ── 4. 恢复 roster
        if (result.roster && result.roster.length > 0) {
          store.rosterList = result.roster.map(m => ({
            name:     m.name     || '',
            gender:   m.gender   || 'Male',
            style:    'T-shirt',
            size:     m.size     != null ? m.size : '',
            quantity: m.quantity != null && m.quantity !== '' ? m.quantity : '',
            message:  m.message  || ''
          }))
        } else {
          store.rosterList = []
        }
        await this.$nextTick()
        if (this.$refs.rosterManager?.loadFromStore) {
          this.$refs.rosterManager.loadFromStore()
        }

        // ── 5. 加载评论
        if (this.$refs.comments?.loadComments) {
          this.$refs.comments.loadComments()
        }

      } catch (e) {
        console.error('Load error:', e)
        this.showToast('Failed to load design', true)
      } finally {
        this.isLoading = false
      }
    },

    // 模型加载完成事件（init 里已处理，这里做兜底）
    onModelLoaded() {
      this.compositeAllTextures()
    },

    async compositeAllTextures() {
      const store = useDesignStore()
      const viewer = this.$refs.viewer3d
      if (!viewer || !viewer.partsMap || viewer.partsMap.size === 0) return

      // 先恢复颜色
      if (typeof viewer.applyColorsToModel === 'function') {
        viewer.applyColorsToModel()
      }

      // 合成每个 part 的纹理
      for (const [part, layers] of store.partLayersMap.entries()) {
        if (!layers || layers.length === 0) continue
        if (!viewer.partsMap.has(part)) continue

        for (const layer of layers) {
          if (!layer.isText && layer.url && !layer.img) {
            await new Promise((resolve) => {
              const img = new Image()
              img.crossOrigin = 'anonymous'
              img.onload = () => { layer.img = img; resolve() }
              img.onerror = () => resolve()
              img.src = layer.url
            })
          }
          if (layer.isText && layer.textContent && !layer.img) {
            const canvas = store._createTextCanvas(
              layer.textContent, layer.textFont || 'Arial',
              layer.textColor || '#000000', layer.fontSize || 80
            )
            const img = new Image()
            img.src = canvas.toDataURL()
            layer.img = img
          }
        }

        viewer.compositePartTexture(part, null, false)
      }
    },

    showToast(msg, isError = false) {
      this.toastMsg = msg
      this.toastError = isError
      if (this._toastTimer) clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => { this.toastMsg = '' }, 3000)
    },
  },
}
</script>

<style scoped>
.review-page {
  font-family: 'Inter', sans-serif;
  background: #ffffff;
  color: #333;
  min-height: 100vh;
}
.loading-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.65);
  display: flex; align-items: center; justify-content: center;
  z-index: 10000;
}
.loading-inner { text-align: center; color: #fff; }
.spinner {
  width: 40px; height: 40px;
  border: 4px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 12px;
}
@keyframes spin { to { transform: rotate(360deg); } }
.toast {
  position: fixed; bottom: 24px; left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px; border-radius: 8px;
  font-size: 0.85rem; font-weight: 500;
  color: #fff; z-index: 10001; pointer-events: none;
}
.toast-success { background: #10b981; }
.toast-error   { background: #ef4444; }
.toast-enter-active, .toast-leave-active { transition: opacity 0.3s, transform 0.3s; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }
.container {
  width: 95%; max-width: 1400px;
  margin: 0 auto; padding: 1rem 0;
}
.page-layout {
  display: flex; gap: 2rem;
  flex-wrap: wrap; align-items: flex-start;
}
.viewer-wrapper { flex: 1; min-width: 280px; }
.right-panel {
  flex: 1; min-width: 320px;
  display: flex; flex-direction: column; gap: 0.8rem;
}
.panel-block { background: #f5f5f5; border-radius: 10px; padding: 1rem; }
.comment-block { background: #eff6ff; }
.roster-block  { background: #f0fdf4; }
@media (max-width: 900px) {
  .page-layout { flex-direction: column; }
  .viewer-wrapper, .right-panel { width: 100%; min-width: 0; }
}
@media (max-width: 850px) {
  .container { width: 100%; padding: 0.5rem; }
  .panel-block { padding: 0.6rem; }
}
</style>